import { useEffect, useState } from 'react';
import { Popover } from '@headlessui/react';
import { Link } from '@loomstateorg/shared-frontend';
import CartIcon from 'components/icons/cart';
import { useFormat } from '@loomstateorg/shared-frontend/hooks/useFormat';
import useViewportWidth from '@loomstateorg/shared-frontend/hooks/useViewportWidth';
import { useCart } from 'frontastic';

export interface Props {
  icon?: any;
  overlayClassName?: string;
  panelClassName?: string;
  rowSurfaceClassName?: string;
}

const DEFAULT_ROW_SURFACE =
  'w-full rounded-md border-0 px-14 py-10 outline-none ring-0 transition-colors hover:bg-nav-highlight active:border-0 active:bg-nav-highlight focus-within:ring-0';

const CartButtonMenu: React.FC<Props> = ({
  icon,
  overlayClassName = 'bg-primary-regular',
  panelClassName = 'bg-site',
  rowSurfaceClassName = DEFAULT_ROW_SURFACE,
}) => {
  const { totalItems: totalCartItems } = useCart();
  const { viewportWidth } = useViewportWidth();
  const { formatMessage: formatCartMessage } = useFormat({ name: 'Cart' });
  const [showPopover, setShowPopover] = useState(false);
  const [isClient, setIsClient] = useState(false);

  useEffect(() => {
    setIsClient(true);
    setShowPopover(viewportWidth >= 1024);
  }, [viewportWidth]);

  return (
    <div className="ml-10 mt-4 size-40" data-testid="cart-menu-item">
      <div
        title={formatCartMessage({ id: 'myCart', defaultMessage: 'My Cart' })}
        className="h-fit cursor-pointer border-primary-regular hover:border-b-2 active:border-0"
      >
        <div className="lg:hidden">
          <Link
            link={'/cart'}
            title={formatCartMessage({ id: 'myCart', defaultMessage: 'My Cart' })}
            data-testid="cart"
          >
            <CartIcon
              className="w-50 text-primary-regular"
              totalCartItems={totalCartItems}
              counterClassName="-translate-y-1/4"
              icon={icon}
            />
          </Link>
        </div>
        {isClient && showPopover && (
          <Popover as="div" className="relative hidden h-fit lg:flex">
            {({ close }) => (
              <>
                <Popover.Button
                  className="border-0 bg-transparent p-0 shadow-none outline-none ring-0 [-webkit-tap-highlight-color:transparent] focus:border-transparent focus:outline-none focus:ring-0 focus-visible:outline-none focus-visible:ring-0 active:border-0 active:outline-none"
                  title={formatCartMessage({ id: 'myCart', defaultMessage: 'My Cart' })}
                  data-testid="cart-toggle"
                >
                  <CartIcon
                    className="hidden w-50 text-primary-regular lg:flex"
                    totalCartItems={totalCartItems}
                    counterClassName="-translate-y-1/4"
                    icon={icon}
                  />
                </Popover.Button>

                <Popover.Overlay className={`fixed inset-0 z-[310] ${overlayClassName} opacity-30`} />
                <Popover.Panel
                  data-component-group="text-container"
                  className={`absolute top-60 z-[310] animate-[appearDropdown_0.15s_ease-in-out] ${panelClassName} shadow-400 lg:-left-62 xl:-left-28`}
                  data-testid="cart-dropdown"
                >
                  <div className="absolute -top-20 z-10 w-31 -translate-x-1/2 overflow-hidden lg:left-85 xl:left-1/2">
                    <div className={`size-21 origin-bottom-left rotate-45 ${panelClassName}`} />
                  </div>
                  <div className="flex w-100 flex-col font-primaryThin text-16 text-primary-regular">
                    <Link
                      link="/cart"
                      className={`group block text-left ${rowSurfaceClassName}`}
                      title={formatCartMessage({ id: 'myCart', defaultMessage: 'My Cart' })}
                      onClick={() => close()}
                      data-testid="view-cart"
                    >
                      <span className="flex min-h-[30px] items-center text-14 leading-none text-primary-regular underline-offset-4 group-hover:underline">
                        {formatCartMessage({ id: 'myCart', defaultMessage: 'My Cart' })}
                      </span>
                    </Link>
                    <Link
                      link="/checkout"
                      className={`group block text-left ${rowSurfaceClassName}`}
                      title={formatCartMessage({ id: 'checkout', defaultMessage: 'Checkout' })}
                      onClick={() => close()}
                      data-testid="checkout"
                    >
                      <span className="flex min-h-[30px] items-center text-14 leading-none text-primary-regular underline-offset-4 group-hover:underline">
                        {formatCartMessage({ id: 'checkout', defaultMessage: 'Checkout' })}
                      </span>
                    </Link>
                  </div>
                </Popover.Panel>
              </>
            )}
          </Popover>
        )}
      </div>
    </div>
  );
};

export default CartButtonMenu;